// IEA Gear View: the front of a housing, laid out from the registry names. Pure, unit tested.
// Words: IEA_GEAR_VIEW_WORDS.md (approved 2026-10-16) section 4. Mockup: MOCKUP_IEA_GEAR_VIEW.html v8.
// Letter = section, number = space top to bottom, a and b = left and right halves of one space.
// A name that does not fit draws as a plain grid in order. It never breaks.

/** One space in a section: one bucket, or two halves (a left, b right). */
export interface GearSpace {
  num: number;
  points: string[];
}

export interface GearSection {
  /** The section letter. Empty for the plain grid. */
  letter: string;
  spaces: GearSpace[];
}

export interface GearLineup {
  sections: GearSection[];
  /** True when every name fit the rule. False draws the plain grid. */
  named: boolean;
  /** Spaces a section is drawn to hold: 6, or more when a section has more. */
  height: number;
}

/** The main breaker's name inside any housing (Gear View words 8). It never sits in the grid. */
export const MAIN_NAME = "MB";

/** A standard MCC section holds 6X of buckets. */
export const SECTION_UNITS = 6;

const NAME = /^([A-Z]{1,2})(\d{1,2})([ab]?)$/;

/** The lineup for a housing's positions, in registry order. */
export function lineupOf(all: readonly string[]): GearLineup {
  const points = all.filter((p) => p !== MAIN_NAME);
  const parsed = points.map((p) => NAME.exec(p));
  if (!points.length || parsed.some((m) => !m)) {
    // Plain grid: one column of spaces in the order given, 6 to a section.
    const sections: GearSection[] = [];
    points.forEach((p, i) => {
      if (i % SECTION_UNITS === 0) sections.push({ letter: "", spaces: [] });
      sections[sections.length - 1]!.spaces.push({ num: (i % SECTION_UNITS) + 1, points: [p] });
    });
    return { sections, named: false, height: SECTION_UNITS };
  }
  const byLetter = new Map<string, Map<number, string[]>>();
  parsed.forEach((m, i) => {
    const letter = m![1]!;
    const num = Number(m![2]);
    if (!byLetter.has(letter)) byLetter.set(letter, new Map());
    const spaces = byLetter.get(letter)!;
    if (!spaces.has(num)) spaces.set(num, []);
    spaces.get(num)!.push(points[i]!);
  });
  const letters = [...byLetter.keys()].sort((a, b) => a.length - b.length || a.localeCompare(b));
  const sections = letters.map((letter) => ({
    letter,
    spaces: [...byLetter.get(letter)!.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([num, pts]) => ({ num, points: [...pts].sort((x, y) => halfRank(x) - halfRank(y)) })),
  }));
  const tallest = Math.max(...sections.map((s) => s.spaces.length));
  return { sections, named: true, height: Math.max(SECTION_UNITS, tallest) };
}

const halfRank = (p: string) => (p.endsWith("b") ? 2 : p.endsWith("a") ? 1 : 0);

/** The walk order: section by section, top to bottom, left half before right. */
export function walkOrder(points: readonly string[]): string[] {
  const rest = lineupOf(points).sections.flatMap((s) => s.spaces.flatMap((sp) => sp.points));
  return points.includes(MAIN_NAME) ? [MAIN_NAME, ...rest] : rest;
}

/** The words that name a bucket's place: "Section E, space 2 (left half)". */
export function placeWords(point: string): string {
  if (point === MAIN_NAME) return "Main breaker";
  const m = NAME.exec(point);
  if (!m) return `Position ${point}`;
  const half = m[3] === "a" ? " (left half)" : m[3] === "b" ? " (right half)" : "";
  return `Section ${m[1]}, space ${Number(m[2])}${half}`;
}

/** Sections from first to last letter, for the header: "sections A to J". */
export function sectionSpan(l: GearLineup): string {
  if (!l.named || !l.sections.length) return `${l.sections.reduce((n, s) => n + s.spaces.length, 0)} positions`;
  const first = l.sections[0]!.letter;
  const last = l.sections[l.sections.length - 1]!.letter;
  return first === last ? `section ${first}` : `sections ${first} to ${last}`;
}

// ---- Which door a housing has ----

export type GearKind = "mcc" | "panel" | "swbd";

/** MCC lineup, panel can, or switchboard lineup, by the housing's code. */
export function gearKindOf(code: string): GearKind | null {
  if (code === "MCC") return "mcc";
  if (["SWBD", "SWGR", "MSB", "SB", "MDP"].includes(code)) return "swbd";
  if (["PNL", "LP", "DP", "PP", "LC"].includes(code)) return "panel";
  return null;
}

// ---- Panels: two sides, odd left, even right (words 4c) ----

export interface PanelSlot {
  point: string;
  /** L left, R right, F full width (a lone space in a split-named panel). */
  side: "L" | "R" | "F";
  /** 1-based row, top to bottom. */
  row: number;
  poles: number;
  /** What the number column shows: "13·15·17", or the name. */
  num: string;
}

const NUMBER = /^(\d{1,3})$/;

/** Where each breaker sits. Numbers: odd left, even right, row (n+1)/2, a 2-pole or 3-pole
 *  breaker spans its slots down its side. Names: a left, b right on the space's row, a lone
 *  space full width. Anything else fills down the sides in order. Never two in one slot. */
export function panelSlots(all: readonly string[], polesOf: (p: string) => number = () => 1): { slots: PanelSlot[]; rows: number } {
  const points = all.filter((p) => p !== MAIN_NAME);
  const taken = new Set<string>();
  const slots: PanelSlot[] = [];
  const free = (side: "L" | "R", row: number, poles: number) => {
    for (let i = 0; i < poles; i++) if (taken.has(`${side}${row + i}`) || taken.has(`F${row + i}`)) return false;
    return true;
  };
  const freeF = (row: number) => !taken.has(`L${row}`) && !taken.has(`R${row}`) && !taken.has(`F${row}`);
  const put = (s: PanelSlot) => {
    for (let i = 0; i < s.poles; i++) taken.add(`${s.side}${s.row + i}`);
    slots.push(s);
  };
  const numbered = points.every((p) => NUMBER.test(p));
  const named = !numbered && points.every((p) => NAME.exec(p));
  const later: string[] = [];
  points.forEach((p, i) => {
    const poles = Math.min(3, Math.max(1, Math.round(polesOf(p) || 1)));
    if (numbered) {
      const n = Number(p);
      const side = n % 2 ? "L" : "R";
      const row = Math.ceil(n / 2);
      if (n > 0 && free(side, row, poles)) {
        const nums = Array.from({ length: poles }, (_, k) => String(n + 2 * k));
        return put({ point: p, side, row, poles, num: nums.join("·") });
      }
    } else if (named) {
      const m = NAME.exec(p)!;
      const row = Number(m[2]);
      if (row > 0 && m[3]) {
        const side = m[3] === "a" ? "L" : "R";
        if (free(side, row, poles)) return put({ point: p, side, row, poles, num: p });
      } else if (row > 0 && freeF(row)) return put({ point: p, side: "F", row, poles: 1, num: p });
    } else {
      // Plain order: circuit i+1, odd left, even right.
      const n = i + 1;
      const side = n % 2 ? "L" : "R";
      const row = Math.ceil(n / 2);
      if (free(side, row, poles)) return put({ point: p, side, row, poles, num: p });
    }
    later.push(p);
  });
  // A name that collides drops to the next open slot, left then right, so nothing hides.
  for (const p of later) {
    for (let row = 1; ; row++) {
      const side = free("L", row, 1) ? "L" : free("R", row, 1) ? "R" : null;
      if (side) {
        put({ point: p, side, row, poles: 1, num: p });
        break;
      }
    }
  }
  const rows = slots.reduce((m, s) => Math.max(m, s.row + s.poles - 1), 0);
  return { slots, rows };
}

/** A panel is walked down the left side, then the right. A full-width space goes with the left. */
export function panelWalk(slots: readonly PanelSlot[]): string[] {
  const by = (side: string) => slots.filter((s) => (side === "L" ? s.side !== "R" : s.side === "R")).sort((a, b) => a.row - b.row);
  return [...by("L"), ...by("R")].map((s) => s.point);
}

/** The words for a breaker's place: "Circuit 13·15·17 · left side, row 7 · 3-pole". */
export function slotWords(s: PanelSlot): string {
  const side = s.side === "L" ? "left side" : s.side === "R" ? "right side" : "full width";
  const head = NUMBER.test(s.point) ? `Circuit ${s.num}` : s.point;
  return `${head} · ${side}, row ${s.row}${s.poles > 1 ? ` · ${s.poles}-pole` : ""}`;
}
